"use client";

/**
 * Se define una línea del carrito POS con datos base del producto y stock disponible.
 */
export type CartLine = {
  id: string;
  inventory_id?: number | null;
  sku?: string;
  name?: string;
  qty: number;
  unit_price: number;
  discount?: number;
  tax_rate?: number;
  available?: number;
};

export type Totals = {
  subtotal: number;
  discount_total: number;
  tax_total: number;
  grand_total: number;
};

export type PosSaleState = {
  isDialogOpen: boolean;
  isOpenCashSessionDialog: boolean;
  terminalId: number | null;
  terminalCode: string | null;
  terminalName: string | null;
  terminalStatus: 'idle' | 'loading' | 'loaded' | 'error';
  cashSessionId: number | null;
  cashSessionStatus: string | null;
  cashSessionOpeningAmount: number | null;
  cashSessionIsStale: boolean;
  cashSessionCanClose: boolean;
  cashSessionBlockReason: string | null;
  cashSessionOpenedByCurrentUser: boolean;
  cashSessionTerminalName: string | null;
  cashSessionOpenedByUsername: string | null;
  cashSessionOpenedByName: string | null;
  cashSessionBusinessDate: string | null;
  cashSessionBusinessTimezone: string | null;
  cashSessionCloseoutHourSnapshot: number | null;
  cashSessionOpenedAt: string | null;
  cashSessionClosedAt: string | null;
  userId: number | null;
  idempotencyKey: string;
  currency: string;
  exchangeRate: number;
  cart: CartLine[];
  totals: Totals;
  customerId: number | null;
  notes: string;
  lastSaleId: number | null;
  lastSaleSavedAt: string | null;
  lastSaleTerminalId: number | null;
  lastSaleCompanyId: number | null;
  lastSaleBranchId: number | null;
  error: string | null;
};

export const initialPosTotals: Totals = {
  subtotal: 0,
  discount_total: 0,
  tax_total: 0,
  grand_total: 0,
};

export const initialPosState: PosSaleState = {
  isDialogOpen: false,
  isOpenCashSessionDialog: false,
  terminalId: null,
  terminalCode: null,
  terminalName: null,
  terminalStatus: 'idle',
  cashSessionId: null,
  cashSessionStatus: null,
  cashSessionOpeningAmount: null,
  cashSessionIsStale: false,
  cashSessionCanClose: false,
  cashSessionBlockReason: null,
  cashSessionOpenedByCurrentUser: false,
  cashSessionTerminalName: null,
  cashSessionOpenedByUsername: null,
  cashSessionOpenedByName: null,
  cashSessionBusinessDate: null,
  cashSessionBusinessTimezone: null,
  cashSessionCloseoutHourSnapshot: null,
  cashSessionOpenedAt: null,
  cashSessionClosedAt: null,
  userId: null,
  idempotencyKey: '',
  currency: 'MXN',
  exchangeRate: 1,
  cart: [],
  totals: initialPosTotals,
  customerId: null,
  notes: '',
  lastSaleId: null,
  lastSaleSavedAt: null,
  lastSaleTerminalId: null,
  lastSaleCompanyId: null,
  lastSaleBranchId: null,
  error: null,
};

/**
 * Recalcula los totales del carrito con descuento e impuesto por línea.
 */
export const computePosTotals = (cart: CartLine[]): Totals => {
  return cart.reduce(
    (acc, line) => {
      const qty = Number(line.qty) || 0;
      const price = Number(line.unit_price) || 0;
      const discount = Number(line.discount) || 0;
      const taxRate = Number(line.tax_rate) || 0;
      const lineSubtotal = qty * price;
      const lineDiscount = discount > 0 ? Math.min(discount, lineSubtotal) : 0;
      const base = lineSubtotal - lineDiscount;
      const lineTax = taxRate > 0 ? base * taxRate : 0;
      acc.subtotal += lineSubtotal;
      acc.discount_total += lineDiscount;
      acc.tax_total += lineTax;
      acc.grand_total += base + lineTax;
      return acc;
    },
    { ...initialPosTotals },
  );
};
